<template>
  <div class="car-month-card-config-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('carMonthCardConfig.title')" :subtitle="$t('carMonthCardConfig.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="showAddDialog()">
          {{ $t('carMonthCardConfig.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 提示条 -->
    <el-alert class="ds-alert" :title="$t('carMonthCardConfig.tips.communityScope')" type="info" :closable="false"
      show-icon />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.cardName" clearable class="w-full"
            :placeholder="$t('carMonthCardConfig.search.cardName')" @keyup.enter.native="search()" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="search()">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetConditions()">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listCardConfigs">
        <template #left>
          <span class="ds-panel__title">{{ $t('carMonthCardConfig.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="cardConfigs" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('carMonthCardConfig.page.empty')" @retry="_listCardConfigs">
        <el-table-column prop="cardName" :label="$t('carMonthCardConfig.table.cardName')" align="left" min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.cardName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="cardMonth" :label="$t('carMonthCardConfig.table.cardMonth')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.cardMonth }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="cardPrice" :label="$t('carMonthCardConfig.table.cardPrice')" align="right" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.cardPrice) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sortNo" :label="$t('carMonthCardConfig.table.sortNo')" align="center" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.sortNo }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('carMonthCardConfig.table.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="showEditDialog(scope.row)">
              {{ $t('carMonthCardConfig.buttons.edit') }}
            </el-button>
            <el-button type="text" size="small" class="ds-btn-danger" @click="deleteConfig(scope.row)">
              {{ $t('carMonthCardConfig.buttons.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 新增 / 修改 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="110px" size="small">
        <el-form-item :label="$t('carMonthCardConfig.form.cardName')" prop="cardName">
          <el-input v-model="form.cardName" maxlength="50" :placeholder="$t('carMonthCardConfig.placeholders.cardName')" />
        </el-form-item>
        <el-form-item :label="$t('carMonthCardConfig.form.cardMonth')" prop="cardMonth">
          <el-input-number v-model="form.cardMonth" :min="1" :max="120" controls-position="right" />
        </el-form-item>
        <el-form-item :label="$t('carMonthCardConfig.form.cardPrice')" prop="cardPrice">
          <el-input v-model="form.cardPrice" :placeholder="$t('carMonthCardConfig.placeholders.cardPrice')" />
        </el-form-item>
        <el-form-item :label="$t('carMonthCardConfig.form.sortNo')" prop="sortNo">
          <el-input-number v-model="form.sortNo" :min="0" :max="9999" controls-position="right" />
        </el-form-item>
        <el-form-item :label="$t('carMonthCardConfig.form.remark')" prop="remark">
          <el-input v-model="form.remark" type="textarea" :rows="2" maxlength="200"
            :placeholder="$t('carMonthCardConfig.placeholders.remark')" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('carMonthCardConfig.buttons.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm()">
          {{ $t('carMonthCardConfig.buttons.confirm') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import {
  listCarMonthCardConfigs,
  saveCarMonthCardConfig,
  updateCarMonthCardConfig,
  deleteCarMonthCardConfig
} from '@/api/fee/carMonthCardConfigApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CarMonthCardConfigList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      communityId: '',
      loading: false,
      saving: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      cardConfigs: [],
      conditions: {
        cardName: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      isEdit: false,
      form: {
        cardId: '',
        cardName: '',
        cardMonth: 1,
        cardPrice: '',
        sortNo: 0,
        remark: ''
      },
      rules: {
        cardName: [{ required: true, message: this.$t('carMonthCardConfig.rules.cardNameRequired'), trigger: 'blur' }],
        cardMonth: [{ required: true, message: this.$t('carMonthCardConfig.rules.cardMonthRequired'), trigger: 'change' }],
        cardPrice: [{ required: true, message: this.$t('carMonthCardConfig.rules.cardPriceRequired'), trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? this.$t('carMonthCardConfig.edit') : this.$t('carMonthCardConfig.add')
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._listCardConfigs()
  },
  methods: {
    async _listCardConfigs() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          communityId: this.communityId,
          cardName: this.conditions.cardName,
          statusCd: '0'
        }
        const { data, total } = await listCarMonthCardConfigs(params)
        this.cardConfigs = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('carMonthCardConfig.page.loadFailed')
        this.$message.error(this.$t('carMonthCardConfig.errors.listFailed'))
      } finally {
        this.loading = false
      }
    },
    search() {
      this.page.current = 1
      this._listCardConfigs()
    },
    resetConditions() {
      this.conditions.cardName = ''
      this.search()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listCardConfigs()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listCardConfigs()
    },
    showAddDialog() {
      this.isEdit = false
      this.form = {
        cardId: '',
        cardName: '',
        cardMonth: 1,
        cardPrice: '',
        sortNo: this.cardConfigs.length + 1,
        remark: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    showEditDialog(row) {
      this.isEdit = true
      this.form = {
        cardId: row.cardId,
        cardName: row.cardName,
        cardMonth: row.cardMonth,
        cardPrice: String(row.cardPrice),
        sortNo: row.sortNo,
        remark: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        this.saving = true
        const params = {
          communityId: this.communityId,
          cardName: this.form.cardName,
          cardMonth: this.form.cardMonth,
          cardPrice: this.form.cardPrice,
          sortNo: this.form.sortNo,
          remark: this.form.remark
        }
        const request = this.isEdit
          ? updateCarMonthCardConfig({ ...params, configId: this.form.cardId })
          : saveCarMonthCardConfig(params)
        request.then(() => {
          this.$message.success(this.$t('common.operationSuccess'))
          this.dialogVisible = false
          this._listCardConfigs()
        }).catch(error => {
          console.error('保存月卡配置失败:', error)
          this.$message.error(error.message || this.$t('carMonthCardConfig.errors.saveFailed'))
        }).finally(() => {
          this.saving = false
        })
      })
    },
    deleteConfig(row) {
      this.$confirm(this.$t('carMonthCardConfig.buttons.delete') + ' ' + row.cardName + ' ?', '提示', {
        type: 'warning'
      }).then(() => {
        return deleteCarMonthCardConfig({
          communityId: this.communityId,
          configId: row.cardId
        })
      }).then(() => {
        this.$message.success(this.$t('common.operationSuccess'))
        this._listCardConfigs()
      }).catch(error => {
        if (error === 'cancel' || error === 'close') {
          return
        }
        console.error('删除月卡配置失败:', error)
        this.$message.error(error.message || this.$t('carMonthCardConfig.errors.deleteFailed'))
      })
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.car-month-card-config-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 提示条 */
.ds-alert {
  margin-bottom: var(--ds-spacing-md);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
